import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, getGuestSession } from "@/lib/api";
import { TypeformIntake } from "@/components/auth/TypeformIntake";
import { CountrySelect, TargetCountriesPicker } from "@/components/forms/CountryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowRight, GraduationCap, Rocket, CheckCircle2, Mail, ShieldCheck, RefreshCw, Sparkles } from "lucide-react";

const STRONG_PW = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const EMPTY = {
  email: "", password: "", name: "", persona: "undergraduate",
  field_of_study: "", target_country: "", academic_level: "", start_year: "",
  country: "", programme_of_interest: "", affiliate_media: "",
};

// Reusable signup flow (email → OTP → HubSpot branch: known=prefilled details /
// unknown=Typeform intake → prefilled details). Used by the Register page and
// the WordPress favorite widget popup so both share one signup experience.
const Req = () => <span aria-hidden="true" className="text-orange font-bold">*</span>;

export function RegisterFlow({ onSuccess, compact = false, onStageChange, onLoginClick }) {
  const { register } = useAuth();
  // stages: "email" | "otp" | "typeform" | "fetching" | "details"
  const [stage, setStage] = useState("email");
  const [form, setForm] = useState(EMPTY);
  const [code, setCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [imported, setImported] = useState(false);

  useEffect(() => { onStageChange?.(stage); }, [stage, onStageChange]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPrefill = (prefill) => {
    if (!prefill) return false;
    setForm((f) => ({
      ...f,
      name: prefill.name || f.name,
      persona: prefill.persona || f.persona,
      field_of_study: prefill.profile?.field_of_study || f.field_of_study,
      target_country: prefill.profile?.target_country || f.target_country,
      academic_level: prefill.profile?.academic_level || f.academic_level,
      start_year: prefill.profile?.start_year || f.start_year,
      country: prefill.hubspot?.country || f.country,
      programme_of_interest: prefill.hubspot?.programme_of_interest || f.programme_of_interest,
      affiliate_media: prefill.hubspot?.affiliate_media || f.affiliate_media,
    }));
    setImported(true);
    return true;
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!form.email) { setError("Please enter your email."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/request-otp", { email: form.email });
      toast.success("Code sent! Check your inbox.");
      setStage("otp");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not send code");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (value) => {
    const c = value ?? code;
    if (c.length !== 6) return;
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, code: c });
      setVerificationToken(data.verification_token);
      if (data.found && data.prefill) {
        applyPrefill(data.prefill);
        toast.success(`Welcome ${data.prefill.name || "back"}! We pre-filled your details.`);
        setStage("details");
      } else {
        toast.success("Email verified! Tell us about your project.");
        setStage("typeform");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Invalid code");
      setCode("");
    } finally { setLoading(false); }
  };

  // HubSpot workflows fill some fields (target country, intake) a few seconds after the
  // Typeform submission: keep polling until they are present (max ~24s).
  const recheckHubspot = async () => {
    let found = false;
    for (let i = 0; i < 8; i++) {
      try {
        const { data } = await api.post("/auth/hubspot-prefill", {
          email: form.email, verification_token: verificationToken,
        });
        if (data.found && data.prefill) {
          applyPrefill(data.prefill);
          found = true;
          const p = data.prefill.profile || {};
          if (p.target_country && (p.start_year || i >= 3)) return true;
        }
      } catch (e) { /* keep trying */ }
      if (i < 7) await new Promise((r) => setTimeout(r, 3000));
    }
    return found;
  };

  const onTypeformDone = async () => {
    setStage("fetching");
    toast.success("Thanks! Fetching your details…");
    await recheckHubspot();
    setStage("details");
  };

  const resend = async () => {
    setError("");
    try {
      await api.post("/auth/request-otp", { email: form.email });
      toast.success("A new code has been sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !STRONG_PW.test(form.password)) { setError("Enter your name and a password with 8+ chars incl. uppercase, lowercase, number and special character."); return; }
    setError(""); setLoading(true);
    try {
      const user = await register({
        email: form.email,
        password: form.password,
        name: form.name,
        persona: form.persona,
        verification_token: verificationToken,
        guest_session_id: getGuestSession(),
        profile: {
          persona: form.persona,
          field_of_study: form.field_of_study,
          target_country: form.target_country,
          academic_level: form.academic_level,
          start_year: form.start_year,
          country: form.country,
          programme_of_interest: form.programme_of_interest,
          affiliate_media: form.affiliate_media,
        },
      });
      onSuccess?.(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const stepNum = { email: 1, otp: 2, typeform: 3, fetching: 3, details: 3 }[stage];
  const totalSteps = stage === "typeform" || stage === "fetching" ? 4 : 3;

  const importedChips = [
    ["Programme of interest", form.programme_of_interest],
    ["Affiliate media", form.affiliate_media],
  ].filter(([, v]) => v);

  return (
    <div className="w-full" data-testid="register-form">
      <div className="eyebrow text-xs text-orange mb-2">Create account · Step {stepNum} of {totalSteps}</div>
      <h1 className={`headline text-navy ${compact ? "text-2xl mb-4" : "text-4xl mb-6"}`}>Join Elite</h1>

      {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-xl" data-testid="register-error">{error}</div>}

      {/* STEP 1 — email */}
      {stage === "email" && (
        <form onSubmit={requestOtp} className="space-y-4 animate-fade-up">
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-orange/10 mb-2">
            <Mail className="h-5 w-5 text-orange shrink-0" />
            <p className="text-sm text-navy">We'll email you a 6-digit code to verify your address.</p>
          </div>
          <div>
            <Label htmlFor="email">Email <Req /></Label>
            <Input id="email" data-testid="reg-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="you@example.com" />
          </div>
          <Button type="submit" data-testid="reg-send-code" disabled={loading} className="w-full h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
            {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <>Send code <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
        </form>
      )}

      {/* STEP 2 — otp */}
      {stage === "otp" && (
        <div className="space-y-5 animate-fade-up">
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-navy/5">
            <ShieldCheck className="h-5 w-5 text-navy shrink-0" />
            <p className="text-sm text-navy">Enter the 6-digit code sent to <b>{form.email}</b>.</p>
          </div>
          <div className="flex justify-center py-2">
            <InputOTP maxLength={6} value={code} data-testid="reg-otp" onChange={(v) => { setCode(v); if (v.length === 6) verifyOtp(v); }}>
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl font-bold rounded-xl border-2" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button data-testid="reg-verify" onClick={() => verifyOtp()} disabled={loading || code.length !== 6} className="w-full h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
            {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : <>Verify email <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={() => { setStage("email"); setCode(""); }} className="text-muted-foreground hover:text-navy">← Change email</button>
            <button type="button" data-testid="reg-resend" onClick={resend} className="font-semibold text-navy hover:text-orange">Resend code</button>
          </div>
        </div>
      )}

      {/* STEP 3a — typeform intake (email unknown in HubSpot) */}
      {stage === "typeform" && (
        <div className="space-y-4 animate-fade-up">
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-orange/10">
            <Sparkles className="h-5 w-5 text-orange shrink-0" />
            <p className="text-sm text-navy">We don't know you yet — tell us about your project so we can tailor Elite to you.</p>
          </div>
          <TypeformIntake email={form.email} onSubmit={onTypeformDone} height={compact ? "56vh" : "72vh"} minHeight={compact ? 380 : 560} />
          <div className="flex items-center justify-between text-sm pt-1">
            <span className="text-muted-foreground truncate">Signing up as <b>{form.email}</b></span>
            <button type="button" data-testid="reg-typeform-continue" onClick={onTypeformDone} className="font-semibold text-navy hover:text-orange shrink-0 ml-2">I've completed the form →</button>
          </div>
        </div>
      )}

      {/* STEP 3b — fetching after typeform */}
      {stage === "fetching" && (
        <div className="py-16 flex flex-col items-center justify-center text-center animate-fade-up" data-testid="reg-fetching">
          <RefreshCw className="h-10 w-10 text-orange animate-spin mb-4" />
          <h3 className="font-display font-extrabold italic text-navy text-lg">Fetching your details…</h3>
          <p className="text-sm text-muted-foreground mt-1">Bringing over everything you just shared.</p>
        </div>
      )}

      {/* STEP 3 — details */}
      {stage === "details" && (
        <form onSubmit={submit} className="space-y-4 animate-fade-up">
          <div className="flex items-center gap-2 text-sm text-green-600 mb-1"><CheckCircle2 className="h-4 w-4" /> {form.email} verified{imported && " · details imported"}</div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label>I am a... <Req /></Label><span data-testid="reg-required-note" className="text-xs text-muted-foreground"><span className="text-orange font-bold">*</span> Required — everything else is optional</span></div>
            <div className="grid grid-cols-2 gap-3">
              {[{ k: "undergraduate", i: GraduationCap, l: "Undergraduate" }, { k: "postgraduate", i: Rocket, l: "Postgraduate" }].map((p) => {
                const Icon = p.i; const active = form.persona === p.k;
                return (
                  <button type="button" key={p.k} data-testid={`reg-persona-${p.k}`} onClick={() => set("persona", p.k)} className={`p-4 rounded-2xl border flex flex-col items-center gap-2 transition-all ${active ? "border-orange bg-orange/5 ring-1 ring-orange" : "border-border hover:border-navy/30"}`}>
                    <Icon className={`h-6 w-6 ${active ? "text-orange" : "text-navy"}`} />
                    <span className="text-sm font-bold text-navy">{p.l}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div><Label htmlFor="name">Full name <Req /></Label><Input id="name" data-testid="reg-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="Jane Doe" /></div>
          <div><Label htmlFor="password">Password <Req /></Label><Input id="password" data-testid="reg-password" type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="8+ chars, upper, lower, number, symbol" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="field">Field of interest</Label><Input id="field" data-testid="reg-field" value={form.field_of_study} onChange={(e) => set("field_of_study", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="e.g. Business" /></div>
            <div><Label>Country of residence</Label><CountrySelect testId="reg-residence-country" value={form.country} onChange={(v) => set("country", v)} /></div>
            <div className="col-span-2"><Label>Target countries <span className="font-normal text-muted-foreground">— choose several or none</span></Label><TargetCountriesPicker testId="reg-country" value={form.target_country} onChange={(v) => set("target_country", v)} /></div>
            <div><Label htmlFor="level">Academic level</Label><Input id="level" value={form.academic_level} onChange={(e) => set("academic_level", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="e.g. Bachelor" /></div>
            <div><Label htmlFor="year">Elite intake</Label><Input id="year" value={form.start_year} onChange={(e) => set("start_year", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="e.g. 2026" /></div>
          </div>

          {importedChips.length > 0 && (
            <div className="rounded-2xl border border-border bg-navy/5 p-4" data-testid="reg-hubspot-extras">
              <div className="eyebrow text-[11px] text-orange mb-2">Imported from HubSpot</div>
              <div className="flex flex-wrap gap-2">
                {importedChips.map(([label, val]) => (
                  <span key={label} className="text-xs bg-white border border-border rounded-full px-3 py-1 text-navy">
                    <span className="text-muted-foreground">{label}:</span> <b>{val}</b>
                  </span>
                ))}
              </div>
            </div>
          )}

          <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
            {loading ? "Creating…" : <>Create account <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
        </form>
      )}

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{" "}
        {onLoginClick ? (
          <button type="button" onClick={onLoginClick} className="font-bold text-navy hover:text-orange" data-testid="to-login">Log in</button>
        ) : (
          <Link to="/login" className="font-bold text-navy hover:text-orange" data-testid="to-login">Log in</Link>
        )}
      </p>
    </div>
  );
}
